import { ComponentPortal } from '@angular/cdk/portal';
import { ChangeDetectorRef, Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { CheckBoxThemeControl } from '../../../appbuilder/controls/checkbox/checkbox.control';
import { ColorpickerThemeControl } from '../../../appbuilder/controls/colorpicker/colorpicker.theme.control';
import { ComboboxThemeControl } from '../../../appbuilder/controls/combobox/combobox.theme.control';
import { FileSelectionThemeControl } from '../../../appbuilder/controls/fileselection/fileselection.theme.control';
import { GenericMenuTab } from '../../../appbuilder/menutabs/generic/generic.menu.tab';
import { BasePanel } from '../../../appbuilder/panels/base.panel';
import { InjectorHelper } from '../../../helpers/injector.helper';
import { PropertyGroupDisplay } from '../../../models/enums/propertygroupdisplay.enum';
import { StyleBase } from '../../../models/layoutbase.model';
import { MenuTabLabelPosition } from '../../../models/menutab/menutab.property.model';
import { Color } from '../../../models/style/color.model';
import { Gradient } from '../../../models/style/gradient.model';
import { PROPERTIES, PROPERTYGROUPS } from '../../../services/dynamic.component.service';
import { LayoutService } from '../../../services/layout.service';
import { MediaService } from '../../../services/media.service';
import { FileExplorerDialog } from '../../fileexplorer/fileexplorer.dialog';
import { IBaseComponent } from '../base.component';

@Component({
    selector: 'evi-svg',
    templateUrl: './svg.control.html',
    styleUrls: ['./svg.control.css']
})
export class SVGControl extends IBaseComponent {
    static Type: any = 'svg';
    static Default = {
        Source: [],
        Type: 'svg',
        Layout: {}
    };
    svg: any;
    svgElement: SVGElement;
    svgStyle: HTMLStyleElement;
    AdditionalItemStyles: any;
    @ViewChild('svgContainer', { read: ElementRef }) svgContainer: ElementRef;

    private static SetAdditionalStyleKeys(elem: any) {
        if (elem) {
            if (elem.id) {
                elem.classList.add(elem.id + '_WFStyle');
            }
            if (elem.children) {
                for (let i = 0; i < elem.children.length; i++) {
                    SVGControl.SetAdditionalStyleKeys(elem.children[i]);
                }
            }
        }
    }

    private static getIDTarget(target: SVGElement) {
        // add to settings!
        while (target) {
            if (target.id) {
                return target;
            }
            if (target.parentElement instanceof SVGElement) {
                target = target.parentElement;
            } else {
                return null;
            }
        }
    }

    constructor(private mediaService: MediaService, private dialog: MatDialog, private sanitizer: DomSanitizer,
        cdRef: ChangeDetectorRef, @Inject(LayoutService.CONTAINER_DATA) public data: any) {
        super(cdRef, data);
        this.EventList.push('SelectionChanged');
    }

    ControlInitialized() {
        this.CreateUrl();
        this.Subscriptions["SVGDSChange"] = this.DataSourceChange.subscribe(() => {
            if (this.LayoutElementValue.UseForBlob || (this.LayoutElementValue.DataSource && this.LayoutElementValue.MediaSource)) {
                this.CreateUrl();
            }
        });
    }

    onLayoutElementChanged() {
        if (!this.Editable) {
            this.CreateUrl();
        }
    }

    CreateUrl() {
        if (this.LayoutElementValue.UseForBlob) {
            this.setSVG(this.DataSourceValue);
        } else if (this.LayoutElementValue.MediaSource) {
            let fileName = this.LayoutElementValue.FileName;
            if (this.LayoutElementValue.DataSource) {
                if (this.DataSourceValue) {
                    fileName = this.DataSourceValue;
                }
            }
            if (typeof fileName === 'string' && fileName.endsWith('.svg')) {
                this.mediaService.LoadImage(this.LayoutElementValue.MediaSource, fileName).subscribe((file) => {
                    this.setSVG(file);
                });
            } else {
                this.setSVG(null);
            }
        }
    }

    setSVG(svgContent: any) {
        if (typeof svgContent === 'string' && svgContent.startsWith('data:')) {
            svgContent = atob(svgContent.split(',')[1]);
        }
        if (svgContent) {
            this.svg = this.sanitizer.bypassSecurityTrustHtml(svgContent);
        } else {
            this.svg = null;
        }
        this.cdRef.detectChanges();
        if (this.svgContainer && this.svgContainer.nativeElement && this.svgContainer.nativeElement.children) {
            for (let i = 0; i < this.svgContainer.nativeElement.children.length; i++) {
                const svgChild = this.svgContainer.nativeElement.children[i];
                if (svgChild instanceof SVGElement) {
                    this.setSVGElement(svgChild);
                    return;
                }
            }
        }
    }

    setSVGElement(elem: any) {
        if (this.svgElement) {
            this.svgElement.removeEventListener('mouseover', this.onMouseOver.bind(this));
            this.svgElement.removeEventListener('mouseout', this.onMouseOut.bind(this));
            this.svgElement.removeEventListener('click', this.onClick.bind(this));
        }
        this.svgStyle = null;
        this.svgElement = elem;
        if (this.svgElement) {
            SVGControl.SetAdditionalStyleKeys(this.svgElement);
            this.svgElement.addEventListener('mouseover', this.onMouseOver.bind(this));
            this.svgElement.addEventListener('mouseout', this.onMouseOut.bind(this));
            this.svgElement.addEventListener('click', this.onClick.bind(this));
            this.svgStyle = document.createElement('style');
            this.svgElement.appendChild(this.svgStyle);
            this.setSVGStyle();
        }
    }

    private getTargetElement(target: SVGElement) {
        if (target) {
            if (this.LayoutElementValue.SelectParentID) {
                return SVGControl.getIDTarget(target);
            } else if (target.id) {
                return target;
            }
        }
        return null;
    }

    onMouseOver(ev: any) {
        if (ev) {
            const target = this.getTargetElement(ev.target);
            if (target) {
                target.classList.add('beSyncHoverStyle');
            }
        }
    }

    onMouseOut(ev: any) {
        if (ev) {
            const target = this.getTargetElement(ev.target);
            if (target) {
                target.classList.remove('beSyncHoverStyle');
            }
        }
    }

    onClick(ev: any) {
        if (ev) {
            const target = this.getTargetElement(ev.target);
            if (target) {
                this.triggerEvent('SelectionChanged', target.id);
            } else {
                this.triggerEvent('SelectionChanged', null);
            }
        }
    }

    editSVG() {
        if (this.Editable && this.LayoutElementValue.MediaSource) {
            const dialogRef = this.dialog.open(FileExplorerDialog, {
                panelClass: 'fileexplorer-dialog-container',
                data: {
                    MediaSourceID: this.LayoutElementValue.MediaSource,
                    ImageInstance: this
                }
            });
            dialogRef.afterClosed().subscribe(result => {
                if (result) {
                    this.DataSource = result;
                }
            });
        }
    }

    deleteSVG() {
        if (this.Editable) {
            this.DataSource = null;
        }
    }

    clearWorkflowStyles() {
        this.AdditionalItemStyles = null;
        this.setSVGStyle();
        super.clearWorkflowStyles();
    }

    SetAdditionalStyles(style: StyleBase, settings: any) {
        if (style && Array.isArray(settings) && settings.length > 0) {
            if (!this.AdditionalItemStyles) {
                this.AdditionalItemStyles = {};
            }
            settings.forEach(id => {
                let list = this.AdditionalItemStyles[id];
                if (list) {
                    list.push(style);
                } else {
                    this.AdditionalItemStyles[id] = [style];
                }
            });
            this.setSVGStyle();
        }
    }

    setSVGStyle() {
        if (this.svgStyle) {
            const svgStyleList = [];
            if (this.LayoutElementValue.HoverColor && Color.IsValid(this.LayoutElementValue.HoverColor)) {
                svgStyleList.push('.beSyncHoverStyle{fill:' + Color.GetStyleText(this.LayoutElementValue.HoverColor) + '}');
            }
            if (this.AdditionalItemStyles) {
                Object.keys(this.AdditionalItemStyles).forEach(k => {
                    const styleList = this.AdditionalItemStyles[k];
                    if (styleList && styleList.length > 0) {
                        const newStyleList = [];
                        styleList.forEach((style: StyleBase) => {
                            const fill = Gradient.getStyleObject(style.BackgroundColor)['background-color'];
                            if (fill) {
                                newStyleList.push('fill:' + fill);
                            }
                        });
                        if (newStyleList.length > 0) {
                            svgStyleList.push('.' + k + '_WFStyle{' + newStyleList.join(';') + '}');
                        }
                    }
                });
            }
            this.svgStyle.innerHTML = svgStyleList.join('\n');
        }
    }
}

export class SVGPanel extends BasePanel {
    static override SIDS = ["0f6bd0a5-7f4c-43d2-b415-6bafe3a61eff"]
    static InitPanel() {
        PROPERTYGROUPS.push({
            SID: "0f6bd0a5-7f4c-43d2-b415-6bafe3a61eff",
            ID: 'svg',
            Caption: '@@SVG',
            Index: 100,
            Content: GenericMenuTab,
            Display: PropertyGroupDisplay.Grid,
            Columns: ['100%'],
            Rows: ['auto', 'auto', 'auto', 'auto', 'auto'],
            CheckVisibility: (item: any) => {
                return item.ElementType == 'svg';
            }
        });
        PROPERTIES.push({
            ID: "UseForBlob",
            Parent: "svg",
            Content: new ComponentPortal(CheckBoxThemeControl),
            InitArgs: {
                Caption: "@@UseForBlob"
            },
            Row: 1
        });

        let mediaService = InjectorHelper.InjectorInstance.get<MediaService>(MediaService);
        mediaService.GetUsable().subscribe((data) => {
            PROPERTIES.push({
                ID: "MediaSource",
                Parent: "svg",
                Content: new ComponentPortal(ComboboxThemeControl),
                Label: "@@SelectMediaSource",
                Row: 2,
                CheckVisibility: (item) => {
                    return !item.UseForBlob;
                },
                InitArgs: {
                    Placeholder: "@@Select",
                    Multiple: false,
                    ItemsSource: data,
                    DisplayMemberPath: 'Caption',
                    ValueMemberPath: 'SID'
                }
            })
        });
        PROPERTIES.push({
            ID: "FileName",
            Parent: "svg",
            Content: new ComponentPortal(FileSelectionThemeControl),
            CheckVisibility: (item) => {
                return !item.UseForBlob && item.MediaSource;
            },
            Row: 3
        });
        PROPERTIES.push({
            ID: "HoverColor",
            Parent: "svg",
            Content: new ComponentPortal(ColorpickerThemeControl),
            Label: "@@HoverColor",
            LabelPosition: MenuTabLabelPosition.Right,
            Row: 4,
            InitArgs: {
                Icon: 'toolbox_properties'
            }
        });
        PROPERTIES.push({
            ID: "SelectParentID",
            Parent: "svg",
            Content: new ComponentPortal(CheckBoxThemeControl),
            InitArgs: {
                Caption: "@@SelectParentID"
            },
            Row: 5
        });
    }
}
